import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames/bind'
import _ from 'lodash'
import { Icon } from 'semantic-ui-react'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const Step = ({
  onStepClick, // if undefined, stepper is read-only (not clickable)
  completed = false,
  stepItem,
  index,
  activeStep,
  prevStepCompleted,
  elmId = 'default',
  viewMode, // allows for clicking on all steps, regardless if completed
  isLastStep = false,
  ...rest
}) => {
  /**
   * In the case of edit mode, we let the user go to any completed step or
   * the next unfinished step (similar to as if they hit Continue on the form).
   * What we don't allow them to do is skip ahead (we want them to hit each step)
   * or click again on same tab (which is pointless).
   */
  const isClickable = useMemo(
    () =>
      onStepClick &&
      index !== activeStep &&
      (completed || prevStepCompleted || viewMode),
    [onStepClick, completed, index, viewMode, prevStepCompleted]
  )

  return (
    <div
      className={cx('step-container')}
      data-elm-id={`step_container_${elmId}`}
    >
      {/* 
          We layout the "connector" to the right of the current button and do it first so the button overlays 
          the connector. All parts must be "position: absolute" for this to work but flex handles it fine.  
          (Using z-index won't work when this is used on a Modal for example - which itself is z-index'd)
      */}
      {!isLastStep && (
        <div
          className={cx('connector-container')}
          data-elm-id={`connector_container_${elmId}`}
        >
          <span
            className={
              (index === activeStep && prevStepCompleted) || completed
                ? cx('connector-line', 'active')
                : cx('connector-line')
            }
          />
        </div>
      )}
      <button
        data-elm-id={`step_button_${elmId}`}
        onClick={
          isClickable
            ? e =>
                onStepClick(e, {
                  completed,
                  stepItem,
                  activeStep: index,
                  ...rest,
                })
            : _.noop
        }
        className={cx('step-button', { clickable: isClickable })}
      >
        <span
          className={cx('content-container')}
          data-elm-id={`content_container_${elmId}`}
        >
          <span
            className={cx('icon-container')}
            data-elm-id={`icon_container_${elmId}`}
          >
            {completed ? (
              <Icon
                circular
                size='large'
                name='check'
                className={cx('checked', { active: activeStep === index })}
              />
            ) : (
              <Icon
                size='large'
                circular
                className={cx({ active: activeStep === index })}
              >
                <span className={cx('icon-text')}>{index + 1}</span>
              </Icon>
            )}
          </span>
          <span
            className={cx('title-container')}
            data-elm-id={`title_container_${elmId}`}
          >
            <span
              data-elm-id={`title_${elmId}`}
              className={
                activeStep === index ? cx('title', 'active') : cx('title')
              }
            >
              {stepItem}
            </span>
          </span>
        </span>
      </button>
    </div>
  )
}

Step.propTypes = {
  onStepClick: PropTypes.func,
  stepItem: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  elmId: PropTypes.string.isRequired,
  prevStepCompleted: PropTypes.bool,
  activeStep: PropTypes.number,
  completed: PropTypes.bool,
  viewMode: PropTypes.bool,
  isLastStep: PropTypes.bool,
}

const Stepper = ({
  activeStep = 0,
  steps,
  className,
  onStepClick,
  elmId,
  viewMode = false,
  size = 'medium',
}) => {
  return (
    <div
      className={cx('default', className)}
      data-elm-id={`wrapper_container_${elmId}`}
    >
      <div
        className={cx('stepper-container', size)}
        data-elm-id={`stepper_container_${elmId}`}
      >
        {steps.map(({ elmId, stepItem, completed, ...rest }, index) => (
          <Step
            elmId={elmId}
            key={stepItem}
            stepItem={stepItem}
            activeStep={activeStep}
            onStepClick={onStepClick}
            completed={completed}
            index={index}
            prevStepCompleted={index > 0 && steps[index - 1].completed}
            viewMode={viewMode}
            isLastStep={index === steps.length - 1}
            {...rest}
          />
        ))}
      </div>
    </div>
  )
}

export default Stepper

Stepper.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.object).isRequired,
  onStepClick: PropTypes.func,
  elmId: PropTypes.string.isRequired,
  activeStep: PropTypes.number,
  className: PropTypes.string,
  viewMode: PropTypes.bool,
  size: PropTypes.string,
}
